import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  UpsertAgentPreferencesBatchRequest,
  UpsertAgentPreferencesBatchResponse,
  UpsertAgentPreferencesRequest,
  UpsertAgentPreferencesResponse
} from '../runtime/transports/AgentTransport'
import { useAgentRuntime } from '../context/AgentProvider'

const AGENT_CONFIG_QUERY_KEY_PREFIX = ['mp-agents', 'configurations'] as const

export const useAgentPreferences = () => {
  const runtime = useAgentRuntime()
  const queryClient = useQueryClient()

  const invalidateConfigurations = () =>
    queryClient.invalidateQueries({
      queryKey: AGENT_CONFIG_QUERY_KEY_PREFIX
    })

  const upsertSingle = useMutation<
    UpsertAgentPreferencesResponse,
    Error,
    { agentName: string; input: UpsertAgentPreferencesRequest }
  >({
    mutationFn: async ({ agentName, input }) => {
      if (!runtime.transport.upsertAgentPreferences) {
        throw new Error('upsertAgentPreferences is not supported by this transport')
      }
      return runtime.transport.upsertAgentPreferences(agentName, input)
    },
    onSuccess: invalidateConfigurations
  })

  const upsertBatch = useMutation<
    UpsertAgentPreferencesBatchResponse,
    Error,
    UpsertAgentPreferencesBatchRequest
  >({
    mutationFn: async (input) => {
      if (!runtime.transport.upsertAgentPreferencesBatch) {
        throw new Error('upsertAgentPreferencesBatch is not supported by this transport')
      }
      return runtime.transport.upsertAgentPreferencesBatch(input)
    },
    onSuccess: invalidateConfigurations
  })

  return {
    upsertSingle,
    upsertBatch
  }
}
